import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardContent,
  Collapse,
  Grid,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Skeleton,
  Popover,
} from "@mui/material";
import { useContentMetadata } from "../hooks/useCustomQuery";
import CloseIcon from "@mui/icons-material/Close";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import LanguageIcon from "@mui/icons-material/Language";
import useResizeObserver from "../hooks/useResizeObserver";
import PosterTile from "./PosterTile";
import ContentCardActions from "./ContentCardActions";
import { formatCount } from "../utils/utils";
import { ExpandableTypography } from "./ExpandableTypography";

const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

function BookCard({
  contentType,
  contentId,
  onClose,
  status,
  onStatusChange,
  fullscreen,
  statusMenuOpenSignal, // forwarded to ContentCardActions so ContentJournal's
  // empty-state link can open the status dropdown (see LibraryModal)
  collapsed = false, // mobile only: shrinks to the poster + title/author/year
  // (see LibraryModal) when the tab content below has been scrolled
}) {
  const { data: metadata, isLoading } = useContentMetadata(
    contentType,
    contentId,
  );
  const [isDirectorOverflowing, setIsDirectorOverflowing] = useState(false);
  const [pageCountInfoAnchorEl, setPageCountInfoAnchorEl] = useState(null);
  const directorRef = useRef(null);
  const { width: directorWidth, height: directorHeight } =
    useResizeObserver(directorRef);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (directorRef.current) {
        setIsDirectorOverflowing(
          directorRef.current.scrollWidth > directorRef.current.offsetWidth,
        );
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [directorWidth, directorHeight, metadata?.persons]);

  const originalLanguage = metadata?.details?.original_language
    ? languageNames.of(metadata?.details?.original_language)
    : "?";

  const formatPublishedYear = (year) => {
    if (year == null) return ""; // safe fallback

    // BCE years are stored as negative, so convert
    if (year < 0) {
      return `${Math.abs(year)} BC`;
    }

    return year.toString();
  };

  const loadingDiv = (
    <Stack direction="column" spacing={1}>
      <Skeleton width="70%" />
      <Skeleton width="60%" />
      <Skeleton width="50%" />
    </Stack>
  );

  const titleDirectorYearDiv = (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={1}
      alignItems="flex-start"
      sx={{ pr: 2 }}
    >
      <Box
        sx={{
          "& > span": {
            marginRight: 2,
          },
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{
            fontWeight: "bold",
            display: "inline-block",
          }}
        >
          {metadata?.title}
        </Typography>
        <Box
          component="span"
          sx={{
            whiteSpace: "nowrap",
          }}
        >
          <Box sx={{ display: "inline-flex" }}>
            <Tooltip
              // Conditionally display tooltip with full name of director in the event it becomes clipped due to overflow
              title={metadata?.persons?.join(", ")}
              disableHoverListener={!isDirectorOverflowing}
              placement="bottom-start"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -14],
                      },
                    },
                  ],
                },
              }}
            >
              <Typography
                ref={directorRef}
                variant="subtitle1"
                component="span"
                sx={{
                  marginRight: 2,
                  maxWidth: {
                    xs: "160px",
                    sm: "260px",
                    md: "360px",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {metadata?.persons?.join(", ")}
              </Typography>
            </Tooltip>
          </Box>
          <Typography
            variant="subtitle1"
            component="span"
            color="text.secondary"
            sx={{ display: "inline-block", marginRight: 2 }}
          >
            <i>
              {formatPublishedYear(metadata?.details?.first_published_year)}
            </i>
          </Typography>
        </Box>
      </Box>
    </Stack>
  );

  const subtitleDiv = metadata?.details?.subtitle ? (
    <Typography variant="body2"> {metadata?.details?.subtitle} </Typography>
  ) : null;

  const bookStatsDiv = (
    <Box
      sx={{
        flexWrap: "wrap",
        display: "flex",
        direction: "row",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
        <ArticleIcon fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          c.{formatCount(metadata?.details?.page_count)} pages
        </Typography>
        <IconButton
          onClick={(event) => setPageCountInfoAnchorEl(event.currentTarget)}
          sx={{ p: 0 }}
        >
          <InfoOutlinedIcon fontSize="small" color="disabled" />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={1}>
        <LanguageIcon fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          {metadata?.details?.original_language ? originalLanguage : "Unknown"}
        </Typography>
      </Stack>
    </Box>
  );

  const genresDiv =
    metadata?.genres?.length > 0 ? (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ pt: "6px" }}>
          <TheaterComedyIcon fontSize="small" />
        </Box>
        <Box
          sx={{
            flexWrap: "wrap",
          }}
        >
          <Chip
            label={metadata.details.is_fiction ? "fiction" : "non-fiction"}
            size="small"
            variant="outlined"
            sx={{
              "&.MuiChip-root": {
                mt: 0.5,
                mb: 0.5,
                mr: 1,
              },
            }}
          />
          {metadata?.genres?.map((genre) => (
            <Chip
              key={genre}
              label={genre.toLowerCase()}
              size="small"
              variant="filled"
              sx={{
                "&.MuiChip-root": {
                  mt: 0.5,
                  mb: 0.5,
                  mr: 1,
                },
              }}
            />
          ))}
        </Box>
      </Stack>
    ) : null;

  return (
    <>
      <Card
        sx={{
          display: "flex",
          position: "relative",
          width: "100%",
          boxShadow: fullscreen ? "none" : "auto",
        }}
      >
        <CardContent
          sx={{
            padding: 2,
            width: "100%",
            "&:last-child": {
              paddingBottom: 2,
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <PosterTile
                contentId={contentId}
                contentType={contentType}
                contentTitle={metadata?.title}
                imageUrl={metadata?.image_url}
                inline
              />
            </Grid>
            <Grid
              item
              xs={9}
              position="relative"
              display="flex"
              flexDirection="column"
            >
              {isLoading ? (
                loadingDiv
              ) : (
                <>
                  <Box flexGrow={1}>
                    <Stack direction="column" spacing={1}>
                      <Stack direction="column" spacing={0.5}>
                        {titleDirectorYearDiv}
                        {/* Tucked into the tighter title/subtitle spacing so
                            it collapses without leaving a stray gap. */}
                        <Collapse in={!collapsed}>{subtitleDiv}</Collapse>
                      </Stack>
                      {/* Everything but the title/author/year collapses on
                          mobile once the tab content below is scrolled — see
                          the `collapsed` prop and LibraryModal. Unmounted once
                          hidden, so the overview's Show more resets to
                          collapsed when the card expands again. */}
                      <Collapse in={!collapsed} unmountOnExit>
                        <Stack direction="column" spacing={1}>
                          {bookStatsDiv}
                          {genresDiv}
                          <ExpandableTypography text={metadata?.overview} />
                        </Stack>
                      </Collapse>
                    </Stack>
                    <Tooltip title="Close">
                      <IconButton
                        onClick={onClose}
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 0,
                          p: 0.5,
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Collapse in={!collapsed}>
                    <Box sx={{ mt: 2 }}>
                      <ContentCardActions
                        status={status}
                        onStatusChange={onStatusChange}
                        contentId={contentId}
                        contentType={contentType}
                        openSignal={statusMenuOpenSignal}
                      />
                    </Box>
                  </Collapse>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Popover
        id="page-count-info-popover"
        sx={{ mt: 1 }}
        open={Boolean(pageCountInfoAnchorEl)}
        anchorEl={pageCountInfoAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={() => setPageCountInfoAnchorEl(null)}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="body2">
            Page count is an average across all editions
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

export default BookCard;
